import { useCallback } from 'react';
import { useAppDispatch } from '../../../../redux/store';
import { openMessageModal, closeMessageModal } from '../../../../redux/globalSlices/genericSlice';
import { deleteRemittanceRecordThunk } from '../../../../redux/services/cashApplicationService';
import type { RemittanceRecord } from '@dill/dill-shared';
import { appDelay } from '@dill/dill-shared';
import { toast } from 'react-toastify';
import { formatCurrency } from '../utils';

/**
 * Confirm, then delete a payment — shared by all three remittance queues.
 *
 * One copy rather than three, because the wording is the safeguard: deleting is the only action in
 * these queues that removes a payment rather than moving it, and a reviewer should read the same
 * sentence wherever they do it.
 */
export const useDeleteRemittance = (supplierId: string | undefined) => {
  const dispatch = useAppDispatch();

  return useCallback(
    (record: RemittanceRecord) => {
      const payer = record.payerName ?? 'Unknown';

      dispatch(
        openMessageModal({
          buttonText: 'Cancel',
          isSecondButton: true,
          secondButtonText: 'Delete',
          modalType: 'WARN',
          title: 'Delete this payment?',
          subTitle:
            `${formatCurrency(record.amount)} from ${payer} will be removed from the review ` +
            `queues. Any deposit it was linked to goes back to Unmatched Deposits. This cannot ` +
            `be undone from the app.`,
          onSecondButtonClick: async () => {
            if (!supplierId) return;
            await appDelay(10);
            dispatch(
              openMessageModal({
                buttonText: '',
                modalType: 'LOADING',
                title: 'Deleting payment',
                subTitle: `Removing the payment from ${payer}...`,
              })
            );
            const result = await dispatch(
              deleteRemittanceRecordThunk({ supplierId, remittanceId: record.id })
            );
            dispatch(closeMessageModal());

            if (result.meta.requestStatus === 'fulfilled') {
              toast.success(`Payment from ${payer} deleted.`, { autoClose: 3000 });
            } else {
              // The server refuses posted payments by name, so show what it said rather than a
              // generic failure — the reviewer needs to know a receipt has to be reversed first.
              toast.error((result.payload as string) ?? 'Failed to delete the payment.', {
                autoClose: 5000,
              });
            }
          },
        })
      );
    },
    [dispatch, supplierId]
  );
};
